/**
 * Career flow tuning (plan §6 M2-22): the flight-axis threshold that cancels auto-dock, the number of station
 * notices kept on screen, and which deferred assets the career loads after the first frame. The values live in
 * career.json, Zod-validated once at import.
 */
import { z } from 'zod'
import type { CareerFlowTuning } from '../../career/flow'
import careerJson from './career.json'

export const careerFlowTuningSchema = z.strictObject({
  /** A flight axis (stick or keys) beyond this counts as flying: it cancels a running auto-dock. */
  flightActiveThreshold: z.number().min(0).max(1),
  /** Station notices kept on screen (the oldest goes first). */
  maxNotices: z.number().int().min(1).max(20),
})

/** career.json: the flow tuning plus the asset prefixes the career loads. */
export const careerJsonSchema = careerFlowTuningSchema.extend({
  /**
   * Deferred asset id prefixes the career loads after the first frame (M2's stations and props). Other deferred
   * prefixes in app.json (M3's `reach.*` models) belong to other modes and are never fetched by the career.
   */
  assetPrefixes: z.array(z.string().min(1)).min(1),
})

export function parseCareerFlowTuning(raw: unknown): CareerFlowTuning {
  const { flightActiveThreshold, maxNotices } = careerJsonSchema.parse(raw)
  return { flightActiveThreshold, maxNotices }
}

export const CAREER_FLOW_TUNING: CareerFlowTuning = parseCareerFlowTuning(careerJson)

/** The deferred asset prefixes the career loads (career.json `assetPrefixes`). */
export const CAREER_ASSET_PREFIXES: readonly string[] = careerJsonSchema.parse(careerJson).assetPrefixes

/** True for an asset id the career loads after the first frame: deferred at boot and under a career prefix. */
export function isCareerAsset(prefixes: readonly string[], deferred: boolean, id: string): boolean {
  if (!deferred) return false
  for (let i = 0; i < prefixes.length; i++) if (id.startsWith(prefixes[i])) return true
  return false
}
